import { z } from 'zod'

import type { AmsTrendMetric } from '@/client'
import { amsDefaultRange } from '@/components/Ams/shared/datePresets'
import { plainSearch } from '@/components/Ams/shared/plainSearch'
import { pruned } from '@/lib/filters'

import {
  DEFAULT_TREND_GRANULARITY,
  METRIC_UNIT,
  TREND_GRANULARITIES,
  TREND_METRICS,
} from './trend/trendOptions'
import { DEFAULT_PAGE_SIZE, PAGE_TABS, defaultTabFor, type GridState } from './types'

/**
 * The AMS View page's URL state, under VentaHub's own keys
 * (`pages/ams_view/AmsViewPage.tsx:320-499, 613-695`) so a VentaHub link
 * still opens the same report.
 *
 * Every key is optional with `.catch(undefined)`: a query string is user
 * input, and a bad value falls back to its default rather than failing the
 * page (rules E9). Defaults are applied at read time and pruned on the way
 * out (`pruneAmsViewSearch`), so the clean URL is the default view.
 *
 * The router JSON-parses each value, so an id list arrives as a number, a
 * JSON array or — from a VentaHub link — a CSV string like `"2,4"`; the
 * list preprocessors below take all three.
 */

/** The Ad groups grid's sort columns (`components/AdGroupTable.tsx:39-50`). */
export const ADGROUP_SORT_KEYS = [
  'spend',
  'sales',
  'asp',
  'acos',
  'roas',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'units',
  'conversion',
] as const
export type AdGroupSortKey = (typeof ADGROUP_SORT_KEYS)[number]

/**
 * The Campaigns grid's sort columns (`components/CampaignTable.tsx:35-44`),
 * without Daily Budget and Used: the budget-usage columns are hidden
 * (decision 2).
 */
export const CAMPAIGN_SORT_KEYS = [
  'spend',
  'sales',
  'asp',
  'acos',
  'impressions',
  'clicks',
  'conversion',
] as const
export type CampaignSortKey = (typeof CAMPAIGN_SORT_KEYS)[number]

export type SortDir = 'asc' | 'desc'

/** Text the router JSON-parsed: `?q=2024` arrives as the number 2024, `?q=true` as a flag. */
function scalarText(value: unknown): unknown {
  return typeof value === 'number' || typeof value === 'boolean' ? String(value) : value
}

/**
 * VentaHub's CSV, a JSON array or a lone value, as a list; numbers and flags
 * become text.
 *
 * Only the CSV is split and trimmed, as VentaHub's `paramList` did on a reload
 * (`AmsViewPage.tsx:373-377`). A JSON array is what VentaWiz wrote from a pick,
 * and VentaHub matched picks raw — options and filter alike use the untrimmed
 * `campaign_name` / `status` (`AmsViewPage.tsx:939-963, 1249-1269`) — so its
 * entries stay exactly as written, or a name ending in a space could never be
 * selected.
 */
function toTextList(value: unknown): unknown {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  if (!Array.isArray(value)) {
    const text = scalarText(value)
    return typeof text === 'string' ? [text] : value
  }
  const items: unknown[] = value
  return items.map(scalarText)
}

/** The same, as non-negative integer ids; an entry that is not one is dropped, as VentaHub ignored it. */
function toIdList(value: unknown): unknown {
  const list = toTextList(value)
  if (!Array.isArray(list)) return list
  const items: unknown[] = list
  return items
    .map((item) => (typeof item === 'string' && item.trim() !== '' ? Number(item) : item))
    .filter((item) => typeof item === 'number' && Number.isInteger(item) && item >= 0)
}

/** An Amazon id or a row key: the router may hand it over as a number. */
function toText(value: unknown): unknown {
  return typeof value === 'number' ? String(value) : value
}

/** A number the router left as text (`?store_id=` from a hand-written link). */
function toNumber(value: unknown): unknown {
  return typeof value === 'string' && value.trim() !== '' ? Number(value) : value
}

/**
 * The chart metrics: known ones only, each once, and on one axis. A link that
 * mixes money and counts keeps the unit of its newest (last) metric, the rule
 * VentaHub applied to every pick (`OverallTrendChart.tsx:612-616`,
 * `TrendChart.tsx:531-539`), so Spend and Clicks never share an axis.
 */
function toMetricList(value: unknown): unknown {
  const list = toTextList(value)
  if (!Array.isArray(list)) return list
  const known: AmsTrendMetric[] = [
    ...new Set(
      list.filter((item): item is AmsTrendMetric =>
        (TREND_METRICS as readonly unknown[]).includes(item),
      ),
    ),
  ]
  const newest = known.at(-1)
  return newest === undefined
    ? known
    : known.filter((metric) => METRIC_UNIT[metric] === METRIC_UNIT[newest])
}

const ids = z
  .preprocess(toIdList, z.array(z.number().int().nonnegative()))
  .optional()
  .catch(undefined)
/** Picks of the second filter row; an entry that is not text, or is empty, is dropped. */
const texts = z
  .preprocess(
    (value) => {
      const list = toTextList(value)
      return Array.isArray(list)
        ? list.filter((item): item is string => typeof item === 'string' && item !== '')
        : list
    },
    z.array(z.string().max(200)),
  )
  .optional()
  .catch(undefined)
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)
/** Free text; VentaHub wrote `q` raw (`AmsViewPage.tsx:648`), so `?q=2024` must stay a search. */
const text = z.preprocess(scalarText, z.string().max(200)).optional().catch(undefined)
const amazonId = z
  .preprocess(toText, z.string().regex(/^[\w.-]{1,64}$/))
  .optional()
  .catch(undefined)
const dir = z.enum(['asc', 'desc']).optional().catch(undefined)

export const amsViewSearchSchema = z.object({
  /** The applied stores, ascending; absent or empty means every Amazon store (`P:320-343`). */
  store_ids: ids.describe(
    'Stores the report covers, by id, ascending; written as CSV (38,144). Nothing loads until one is picked and Go is pressed.',
  ),
  /** A VentaHub bookmark's single store, folded into `store_ids` and never written (`P:323-341`). */
  store_id: z
    .preprocess(toNumber, z.number().int().nonnegative())
    .optional()
    .catch(undefined)
    .describe(
      'Legacy VentaHub single store, folded into store_ids on the first write and never written.',
    ),
  /** The applied range; absent means the default window (`P:384-393`). */
  start: day.describe(
    'First day of the period, YYYY-MM-DD; default the 1st of this month (in the first five days of a month, a month before today).',
  ),
  end: day.describe('Last day of the period, YYYY-MM-DD; default today.'),
  /** The applied brands in click order; `0` is Unbranded (`P:413`). */
  b: ids.describe(
    'Brands to include, by id in click order, CSV; 0 is Unbranded. Absent or every brand means no brand filter.',
  ),
  /** The applied search (`P:417`). */
  q: text.describe('The applied search over ad group, ASIN and keyword text.'),
  /** The Go gate, armed by the first Go (`P:448-455`). */
  go: z
    .preprocess(toNumber, z.literal(1))
    .optional()
    .catch(undefined)
    .describe(
      'The Go gate: 1 once Go has been pressed. The ad group list loads only with go=1 or ad_group_id, and a store.',
    ),
  /** The second filter row: campaigns, statuses, ad-group types, targeting types (`P:465-472`). */
  c: texts.describe(
    'Campaign names to narrow the loaded ad groups to, CSV; absent means every campaign.',
  ),
  st: texts.describe(
    'Ad group statuses to narrow to, CSV (ENABLED, PAUSED…); absent means every status.',
  ),
  ty: texts.describe('Ad group types to narrow to, CSV; absent means every type.'),
  tg: texts.describe('Targeting types to narrow to, CSV; absent means every targeting type.'),
  /** The Ad groups sort (`P:494-499`). */
  sk: z
    .enum(ADGROUP_SORT_KEYS)
    .optional()
    .catch(undefined)
    .describe('Column the Adgroups grid is sorted by; default spend.'),
  sd: dir.describe('Sort direction of the Adgroups grid; default desc.'),
  /** The Campaigns sort (`P:1373-1374`; VentaHub kept it in state). */
  csk: z
    .enum(CAMPAIGN_SORT_KEYS)
    .optional()
    .catch(undefined)
    .describe('Column the Campaigns grid is sorted by; default spend.'),
  csd: dir.describe('Sort direction of the Campaigns grid; default desc.'),
  /** The active tab; anything else (Placements included) falls back to the default. */
  tab: z
    .enum(PAGE_TABS)
    .optional()
    .catch(undefined)
    .describe('The active tab; default adgroups, or asins once an ad group is drilled.'),
  /** The drilled ad group's Amazon id; it also arms the Go gate (`P:448-455`). */
  ad_group_id: amazonId.describe(
    'Amazon id of the drilled ad group; opens its detail tabs and arms the Go gate.',
  ),
  /** The Targets → Search Terms cross-drill keyword (`P:492`). */
  target: z
    .preprocess(toText, z.string().min(1).max(500))
    .optional()
    .catch(undefined)
    .describe(
      'Keyword text of the Targets to Search Terms cross-drill: search terms are narrowed to it.',
    ),
  /** The page chart: metrics, buckets and an overriding range (`OverallTrendChart.tsx:285-348`). */
  cm: z
    .preprocess(toMetricList, z.array(z.enum(TREND_METRICS)).min(1))
    .optional()
    .catch(undefined)
    .describe('Metrics the overall trend chart plots, CSV, one unit at a time; default spend.'),
  cg: z
    .enum(TREND_GRANULARITIES)
    .optional()
    .catch(undefined)
    .describe('Bucket size of the overall trend chart; default daily.'),
  cs: day.describe('First day of a chart-only range that overrides the page dates, YYYY-MM-DD.'),
  ce: day.describe('Last day of a chart-only range that overrides the page dates, YYYY-MM-DD.'),
  /** Analytics' money currency when the scope holds several. */
  acur: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe(
      'Currency code (EUR, GBP…) the Analytics money charts use when the report holds several.',
    ),
  /** The active grid: page (1-based), size, expanded row, sort, direction, search. */
  page: z
    .preprocess(toNumber, z.number().int().positive())
    .optional()
    .catch(undefined)
    .describe('Page of the active grid, 1-based; default 1.'),
  size: z
    .union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)])
    .optional()
    .catch(undefined)
    .describe('Rows per page of the active grid; default 50.'),
  open: z
    .preprocess(toText, z.string().min(1).max(500))
    .optional()
    .catch(undefined)
    .describe('Id of the expanded row of the active grid.'),
  gs: z
    .string()
    .min(1)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('Column the active detail grid is sorted by; default spend.'),
  gd: dir.describe('Sort direction of the active detail grid; default desc.'),
  gq: text.describe("Search typed in the active detail grid's own search box."),
})

export type AmsViewSearch = z.infer<typeof amsViewSearchSchema>

/**
 * Clears the grid's state; spread into every tab change, drill change and Go,
 * where VentaHub remounted the table and so reset it.
 */
export const CLEAR_GRID = {
  page: undefined,
  size: undefined,
  open: undefined,
  gs: undefined,
  gd: undefined,
  gq: undefined,
} as const satisfies Partial<AmsViewSearch>

/** The grid on screen, with the defaults applied (`MetricTable.tsx:213-222`). */
export function gridStateOf(search: AmsViewSearch): GridState {
  return {
    page: search.page ?? 1,
    size: search.size ?? DEFAULT_PAGE_SIZE,
    open: search.open,
    sort: search.gs,
    dir: search.gd,
    q: search.gq,
  }
}

/** A grid change as the URL patch that makes it; a key present with `undefined` clears. */
export function gridSearchPatch(patch: Partial<GridState>): Partial<AmsViewSearch> {
  const out: Partial<AmsViewSearch> = {}
  if ('page' in patch) out.page = patch.page
  if ('size' in patch) out.size = patch.size
  if ('open' in patch) out.open = patch.open
  if ('sort' in patch) out.gs = patch.sort
  if ('dir' in patch) out.gd = patch.dir
  if ('q' in patch) out.gq = patch.q
  return out
}

function sameList(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index])
}

/**
 * The URL as written: `lib/filters#pruned` (no `undefined`, `''`, `[]` or
 * `page: 1`) plus every key at its resting value — the tab the drill state
 * opens on, spend-descending sorts, the chart's Spend by day, 50 rows, and the
 * default window — and never the legacy `store_id`, which VentaHub dropped
 * on its first write (`P:613-665`).
 */
export function pruneAmsViewSearch(search: AmsViewSearch, today: Date = new Date()): AmsViewSearch {
  const out = pruned({ ...search, store_id: undefined })
  const drilled = !!out.ad_group_id
  if (out.tab === defaultTabFor(drilled)) delete out.tab
  if (out.sk === 'spend') delete out.sk
  if (out.sd === 'desc') delete out.sd
  if (out.csk === 'spend') delete out.csk
  if (out.csd === 'desc') delete out.csd
  if (out.cm && sameList(out.cm, ['spend'])) delete out.cm
  if (out.cg === DEFAULT_TREND_GRANULARITY) delete out.cg
  if (out.size === DEFAULT_PAGE_SIZE) delete out.size
  const range = amsDefaultRange(today)
  if (out.start === range.from && out.end === range.to) {
    delete out.start
    delete out.end
  }
  return out
}

/**
 * The route's search middleware: the URL written in VentaHub's plain form —
 * `store_ids=38,144`, `b=…`, `st=ENABLED,PAUSED`, `ad_group_id=518…`, and the
 * grid's expanded row and search (`open=1821`, `gq=123`) — rather than the
 * router's JSON (`shared/plainSearch.ts`; VentaHub's writer is
 * `AmsViewPage.tsx:614-694`). The schema above reads both, so links written
 * either way open the same report.
 */
export const plainAmsViewSearch = plainSearch<AmsViewSearch>({
  idLists: ['store_ids', 'b'],
  textLists: ['c', 'st', 'ty', 'tg', 'cm'],
  wholeNumberTexts: ['ad_group_id', 'target', 'q', 'gq', 'open'],
})
